import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Select,
  Input,
  Form,
  Space,
  message,
  Card,
  Tag,
  Descriptions,
  Row,
  Col,
  Divider,
  Grid,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  PhoneOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import api from "../api/Api";
import "./RChamada.css";

const { Option } = Select;
const { TextArea } = Input;
const { Search } = Input;
const { useBreakpoint } = Grid;

const GestaoChamadas = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const [chamadas, setChamadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedChamada, setSelectedChamada] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");

  const [tipo, setTipo] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const userStored = JSON.parse(localStorage.getItem("user"));

  // ─── Estados para automação ─────────────────────────────────────────────────
  const [loadingDados, setLoadingDados] = useState(false);
  const [camposHabilitados, setCamposHabilitados] = useState({
    nome: false,
    municipio: false,
    bairro: false,
    id_provincia: false,
  });

  // ─── API helpers ────────────────────────────────────────────────────────────

  const fetchChamadas = async () => {
    setLoading(true);
    try {
      const res = await api.ListChamadaRegistada();
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.chamadas)
        ? res.chamadas
        : [];
      setChamadas(data);
    } catch {
      message.error("Erro ao carregar chamadas");
      setChamadas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProvincias = async () => {
    try {
      const res = await api.ListProvincia();
      setProvincias(res);
    } catch {
      message.error("Erro ao carregar províncias");
    }
  };

  const fetchMunicipios = async (id_provincia) => {
    if (!id_provincia) {
      setMunicipios([]);
      return;
    }

    setLoadingMunicipios(true);
    try {
      const dados={id_provincia: id_provincia};
      const res = await api.ListMunicipio(dados) ;
      const data = Array.isArray(res) ? res : [];
      setMunicipios(data);
    } catch {
      message.error("Erro ao carregar municípios");
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  };

  const fetchPlanos = async (tipoSelecionado) => {
    try {
      let res = [];
      if (tipoSelecionado === "Informação") res = await api.ListInformacao();
      if (tipoSelecionado === "Reclamação") res = await api.ListReclamacao();
      if (tipoSelecionado === "Solicitação") res = await api.ListSolicitacao();
      setPlanos(res);
    } catch {
      message.error("Erro ao carregar planos");
    }
  };

  const fetchPacotes = async (id_plano, tipoSelecionado) => {
    try {
      let res = [];
      const dados = { id: id_plano };
      if (tipoSelecionado === "Informação")
        res = await api.ListPacoteInformacao(dados);
      if (tipoSelecionado === "Reclamação")
        res = await api.ListPacoteReclamacao(dados);
      if (tipoSelecionado === "Solicitação")
        res = await api.ListPacoteSolicitacao(dados);
      setPacotes(res);
    } catch {
      message.error("Erro ao carregar pacotes");
    }
  };

  // ─── Buscar dados do cliente pelo telefone ─────────────────────────────────
  const fetchDadosCliente = async (telefone) => {
    const apenasNumeros = telefone.replace(/\D/g, "");
    if (apenasNumeros.length !== 9) return;

    setLoadingDados(true);
    try {
      const res = await api.ListDados({ telefone: apenasNumeros });

      if (res && res.nome) {
        // Cliente encontrado - preencher campos
        const novoCamposHabilitados = {
          nome: !res.nome || res.nome === "",
          municipio: !res.municipio || res.municipio === "",
          bairro: !res.bairro || res.bairro === "",
          id_provincia: !res.id_provincia,
        };

        setCamposHabilitados(novoCamposHabilitados);

        // Preencher os campos do formulário
        form.setFieldsValue({
          nome: res.nome || "",
          municipio: res.municipio || undefined,
          bairro: res.bairro || "",
          id_provincia: res.id_provincia || undefined,
        });

        // Se tiver província, buscar municípios
        if (res.id_provincia) {
          await fetchMunicipios(res.id_provincia);
        }

        message.success("Dados do cliente carregados automaticamente");
      } else {
        // Cliente não encontrado - habilitar todos os campos
        setCamposHabilitados({
          nome: true,
          municipio: true,
          bairro: true,
          id_provincia: true,
        });

        // Limpar os campos
        form.setFieldsValue({
          nome: "",
          municipio: undefined,
          bairro: "",
          id_provincia: undefined,
        });

        setMunicipios([]);

        message.info("Cliente não encontrado. Preencha os dados manualmente.");
      }
    } catch (error) {
      // Em caso de erro, habilitar todos os campos
      setCamposHabilitados({
        nome: true,
        municipio: true,
        bairro: true,
        id_provincia: true,
      });

      setMunicipios([]);

      message.warning("Erro ao buscar dados. Preencha os dados manualmente.");
    } finally {
      setLoadingDados(false);
    }
  };

  // ─── Handler para mudança no campo telefone ─────────────────────────────────
  const handleTelefoneChange = (e) => {
    const valor = e.target.value;
    form.setFieldsValue({ telefone: valor });

    const apenasNumeros = valor.replace(/\D/g, "");

    if (apenasNumeros.length === 9) {
      fetchDadosCliente(valor);
    } else if (apenasNumeros.length < 9) {
      setCamposHabilitados({
        nome: true,
        municipio: true,
        bairro: true,
        id_provincia: true,
      });
    }
  };

  // ─── Handler para mudança de província ──────────────────────────────────────
  const handleProvinciaChange = async (id_provincia) => {
    form.setFieldsValue({
      id_provincia,
      municipio: undefined, // Limpar município ao mudar província
    });

    // Buscar municípios da nova província
    await fetchMunicipios(id_provincia);
  };

  useEffect(() => {
    fetchChamadas();
    fetchProvincias();
  }, []);

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (tipo === "Indisponibilidade") {
        await api.AddIndisponibilidade({
          nome: values.nome,
          telefone: values.telefone,
          ocorrencia: values.ocorrencia,
          obs: values.obs,
          municipio: values.municipio,
          id_provincia: values.id_provincia,
          agente: userStored.nome,
        });
      } else {
        const body = {
          nome: values.nome,
          telefone: values.telefone,
          telefone2: values.telefone2,
          bairro: values.bairro,
          obs: values.obs,
          municipio: values.municipio, // Envia a descrição, não o ID
          id_provincia: values.id_provincia,
          agente: userStored.nome,
        };
        if (tipo === "Informação") body.id_informacoes = values.id_pacote;
        if (tipo === "Reclamação") body.id_reclamacoes = values.id_pacote;
        if (tipo === "Solicitação") body.id_solicitacoes = values.id_pacote;
        await api.AddChamadaMovicel(body);
      }
      message.success("Chamada adicionada com sucesso!");
      setModalVisible(false);
      form.resetFields();
      setTipo(null);
      setPlanos([]);
      setPacotes([]);
      setMunicipios([]);
      setCamposHabilitados({
        nome: false,
        municipio: false,
        bairro: false,
        id_provincia: false,
      });
      fetchChamadas();
    } catch {
      message.error("Erro ao adicionar chamada");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setTipo(null);
    setPlanos([]);
    setPacotes([]);
    setMunicipios([]);
    setCamposHabilitados({
      nome: false,
      municipio: false,
      bairro: false,
      id_provincia: false,
    });
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case "Reclamação":
        return "red";
      case "Informação":
        return "blue";
      case "Solicitação":
        return "green";
      case "Indisponibilidade":
        return "orange";
      default:
        return "default";
    }
  };

  const filteredChamadas = useMemo(() => {
    if (!searchTerm) return chamadas;
    const term = searchTerm.toLowerCase();
    return chamadas.filter(
      (c) =>
        c.nome?.toLowerCase().includes(term) || c.telefone?.includes(term)
    );
  }, [chamadas, searchTerm]);

  // ─── Colunas responsivas ────────────────────────────────────────────────────

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      render: (text, record) =>
        isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <strong>{text}</strong>
            <span style={{ color: "#666", fontSize: 12 }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {record.municipio}
              {record.provincia ? ` - ${record.provincia}` : ""}
            </span>
            <Tag
              color={getTipoColor(record.tipo_chamada)}
              style={{ width: "fit-content" }}
            >
              {record.tipo_chamada?.toUpperCase()}
            </Tag>
          </div>
        ) : (
          <strong>{text}</strong>
        ),
    },
    ...(!isMobile
      ? [
          {
            title: "Localização",
            key: "localizacao",
            render: (_, record) => (
              <div>
                <div>{record.municipio}</div>
                <small style={{ color: "#666" }}>{record.provincia}</small>
              </div>
            ),
          },
          {
            title: "Tipo",
            dataIndex: "tipo_chamada",
            key: "tipo_chamada",
            render: (tipo) => (
              <Tag color={getTipoColor(tipo)}>{tipo?.toUpperCase()}</Tag>
            ),
          },
        ]
      : []),
    ...(!isMobile && !isTablet
      ? [
          {
            title: "Plano",
            dataIndex: "plano",
            key: "plano",
            render: (plano) => plano || "-",
          },
          {
            title: "Pacote / Ocorrência",
            dataIndex: "pacote",
            key: "pacote",
            render: (pacote) => pacote || "-",
          },
        ]
      : []),
    {
      title: "Data",
      dataIndex: "data",
      key: "data",
      render: (data) => (
        <Space>
          <CalendarOutlined />
          <span style={{ whiteSpace: "nowrap" }}>{data}</span>
        </Space>
      ),
    },
    {
      title: "",
      key: "acoes",
      width: isMobile ? 60 : 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedChamada(record);
            setViewVisible(true);
          }}
          style={{ padding: isMobile ? 0 : undefined }}
        >
          {!isMobile && "Detalhes"}
        </Button>
      ),
    },
  ];

  // ─── Layout helpers ──────────────────────────────────────────────────────────

  const modalWidth = isMobile ? "100%" : isTablet ? 560 : 700;
  const colFull = 24;
  const colHalf = isMobile ? 24 : 12;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        padding: isMobile ? "12px 8px" : "24px",
        minHeight: "100vh",
        overflowY: "scroll",
        boxSizing: "border-box",
      }}
    >
      <Card
        title={
          <Space>
            <PhoneOutlined />
            {isMobile ? "Chamadas" : "Gestão de Chamadas"}
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
            size={isMobile ? "small" : "middle"}
          >
            {isMobile ? "Nova" : "Nova Chamada"}
          </Button>
        }
        bodyStyle={{ padding: isMobile ? "8px" : "24px" }}
      >
        <div style={{ marginBottom: 16 }}>
          <Search
            allowClear
            enterButton={
              <Button type="primary" icon={<SearchOutlined />}>
                {!isMobile && "Pesquisar"}
              </Button>
            }
            size={isMobile ? "middle" : "large"}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={(value) => setSearchTerm(value)}
            style={{ width: "100%", maxWidth: isMobile ? "100%" : 400 }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
            Mostrando {filteredChamadas.length} de {chamadas.length} chamadas
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredChamadas}
          loading={loading}
          rowKey="id"
          size={isMobile ? "small" : "middle"}
          scroll={{ x: "max-content" }}
          pagination={{
            size: isMobile ? "small" : "default",
            showSizeChanger: !isMobile,
            showTotal: (total, range) =>
              isMobile
                ? `${range[0]}-${range[1]} / ${total}`
                : `${range[0]}-${range[1]} de ${total} chamadas`,
          }}
        />
      </Card>

      {/* ── Modal de Adição ───────────────────────────────────────────────────── */}
      <Modal
        className="modal-chamadas"
        title={
          <Space>
            <PlusOutlined />
            Nova Chamada
          </Space>
        }
        open={modalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Registrar"
        cancelText="Cancelar"
        width={modalWidth}
        style={isMobile ? { top: 0, margin: 0, maxWidth: "100vw" } : {}}
        styles={
          isMobile
            ? { body: { padding: "12px 8px" }, content: { borderRadius: 0 } }
            : {}
        }
        confirmLoading={submitting}
      >
        <Divider style={{ margin: "8px 0 16px" }} />
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Row gutter={[12, 0]}>
            {/* 🔹 TELEFONE (FULL) - COM AUTOMAÇÃO */}
            <Col span={colFull}>
              <Form.Item
                label={
                  <Space>
                    <span>Telefone</span>
                    {loadingDados && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{ fontSize: 14, color: "#1890ff" }}
                            spin
                          />
                        }
                      />
                    )}
                  </Space>
                }
                name="telefone"
                rules={[{ required: true, message: "Digite o telefone" }]}
                extra={
                  loadingDados
                    ? "Buscando dados do cliente..."
                    : "Os dados serão carregados automaticamente ao digitar 9 dígitos"
                }
              >
                <Input
                  placeholder="Digite o telefone (9 dígitos)"
                  prefix={<PhoneOutlined />}
                  onChange={handleTelefoneChange}
                  maxLength={15}
                />
              </Form.Item>
            </Col>

            {/* 🔹 Tipo */}
            <Col span={colHalf}>
              <Form.Item
                label="Tipo de Chamada"
                name="tipo"
                rules={[{ required: true, message: "Selecione o tipo" }]}
              >
                <Select
                  placeholder="Selecione o tipo"
                  onChange={(value) => {
                    setTipo(value);
                    setPacotes([]);
                    setPlanos([]);
                    form.setFieldsValue({
                      id_plano: undefined,
                      id_pacote: undefined,
                    });
                    if (value !== "Indisponibilidade") fetchPlanos(value);
                  }}
                >
                  <Option value="Reclamação">Reclamação</Option>
                  <Option value="Informação">Informação</Option>
                  <Option value="Solicitação">Solicitação</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* Plano + Pacote */}
            {tipo && tipo !== "Indisponibilidade" && (
              <>
                <Col span={colHalf}>
                  <Form.Item
                    label="Plano"
                    name="id_plano"
                    rules={[{ required: true, message: "Selecione o plano" }]}
                  >
                    <Select
                      placeholder="Selecione o plano"
                      onChange={(id) => fetchPacotes(id, tipo)}
                    >
                      {planos.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.plano}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={colHalf}>
                  <Form.Item
                    label="Pacote"
                    name="id_pacote"
                    rules={[
                      { required: true, message: "Selecione o pacote" },
                    ]}
                  >
                    <Select placeholder="Selecione o pacote">
                      {pacotes.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.pacote}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Ocorrência */}
            {tipo === "Indisponibilidade" && (
              <Col span={colFull}>
                <Form.Item
                  label="Ocorrência"
                  name="ocorrencia"
                  rules={[
                    { required: true, message: "Descreva a ocorrência" },
                  ]}
                >
                  <Input placeholder="Descreva a ocorrência" />
                </Form.Item>
              </Col>
            )}

            {/* 🔹 LOCALIZAÇÃO - COM AUTOMAÇÃO COMPLETA */}
            <Col span={colHalf}>
              <Form.Item
                label={
                  <Space>
                    <span>Província</span>
                    {!camposHabilitados.id_provincia && (
                      <Tag color="green" style={{ fontSize: 10 }}>
                        Auto
                      </Tag>
                    )}
                  </Space>
                }
                name="id_provincia"
                rules={[
                  { required: true, message: "Selecione a província" },
                ]}
              >
                <Select
                  placeholder="Selecione a província"
                  disabled={!camposHabilitados.id_provincia}
                  onChange={handleProvinciaChange}
                >
                  {provincias.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.descricao}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={colHalf}>
              <Form.Item
                label={
                  <Space>
                    <span>Município</span>
                    {!camposHabilitados.municipio && (
                      <Tag color="green" style={{ fontSize: 10 }}>
                        Auto
                      </Tag>
                    )}
                    {loadingMunicipios && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                            spin
                          />
                        }
                      />
                    )}
                  </Space>
                }
                name="municipio"
                rules={[{ required: true, message: "Selecione o município" }]}
              >
                <Select
                  placeholder={
                    loadingMunicipios
                      ? "Carregando municípios..."
                      : municipios.length === 0
                      ? "Selecione primeiro a província"
                      : "Selecione o município"
                  }
                  disabled={!camposHabilitados.municipio}
                  loading={loadingMunicipios}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {municipios.map((m, index) => (
                    <Option key={index} value={m.descricao}>
                      {m.descricao}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={colHalf}>
              <Form.Item
                label={
                  <Space>
                    <span>Bairro</span>
                    {!camposHabilitados.bairro && (
                      <Tag color="green" style={{ fontSize: 10 }}>
                        Auto
                      </Tag>
                    )}
                  </Space>
                }
                name="bairro"
                rules={[{ required: true, message: "Digite o bairro" }]}
              >
                <Input
                  placeholder="Digite o bairro"
                  prefix={<EnvironmentOutlined />}
                  readOnly={!camposHabilitados.bairro}
                  style={{
                    backgroundColor: !camposHabilitados.bairro
                      ? "#f5f5f5"
                      : "white",
                  }}
                />
              </Form.Item>
            </Col>

            {/* 🔹 NOME - COM READONLY CONDICIONAL */}
            <Col span={colHalf}>
              <Form.Item
                label={
                  <Space>
                    <span>Nome</span>
                    {!camposHabilitados.nome && (
                      <Tag color="green" style={{ fontSize: 10 }}>
                        Auto
                      </Tag>
                    )}
                  </Space>
                }
                name="nome"
                rules={[{ required: true, message: "Digite o nome" }]}
              >
                <Input
                  placeholder="Digite o nome"
                  prefix={<UserOutlined />}
                  readOnly={!camposHabilitados.nome}
                  style={{
                    backgroundColor: !camposHabilitados.nome
                      ? "#f5f5f5"
                      : "white",
                  }}
                />
              </Form.Item>
            </Col>

            {/* Telefone alternativo */}
            <Col span={colHalf}>
              <Form.Item label="Telefone alternativo" name="telefone2">
                <Input
                  placeholder="Digite o telefone"
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </Col>

            {/* Observações */}
            <Col span={colFull}>
              <Form.Item label="Observações" name="obs">
                <TextArea
                  rows={3}
                  placeholder="Escreva observações (opcional)"
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Modal de Visualização ─────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Detalhes da Chamada
          </Space>
        }
        open={viewVisible}
        onCancel={() => setViewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewVisible(false)}>
            Fechar
          </Button>,
        ]}
        width={isMobile ? "100%" : 600}
        style={isMobile ? { top: 0, margin: 0, maxWidth: "100vw" } : {}}
        styles={
          isMobile
            ? { body: { padding: "12px 8px" }, content: { borderRadius: 0 } }
            : {}
        }
      >
        {selectedChamada && (
          <Descriptions
            column={1}
            bordered
            size={isMobile ? "small" : "default"}
            labelStyle={isMobile ? { width: "38%" } : {}}
          >
            <Descriptions.Item label="Nome">
              <Space>
                <UserOutlined />
                {selectedChamada.nome}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Telefone">
              <Space>
                <PhoneOutlined />
                {selectedChamada.telefone}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Localização">
              <Space>
                <EnvironmentOutlined />
                {selectedChamada.municipio} - {selectedChamada.provincia}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Tipo">
              <Tag color={getTipoColor(selectedChamada.tipo_chamada)}>
                {selectedChamada.tipo_chamada}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Plano">
              {selectedChamada.plano || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Pacote / Ocorrência">
              {selectedChamada.pacote || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Data">
              <Space>
                <CalendarOutlined />
                {selectedChamada.data}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Observações">
              {selectedChamada.obs || "Nenhuma observação"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default GestaoChamadas;